/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useMemo } from 'react'

import Dropdown, { type DropdownOption } from '@/components/ui/dropdown/Dropdown'
import { useCompany } from '@/hooks'
import { companyLabelWithHierarchy } from '@/utils/companyHierarchy'

/** The one company a geofence screen works on: the shared company dropdown, as Cold Storage uses it. */
export const CompanyPicker = ({
  companyId,
  onChange,
}: {
  companyId: number | undefined
  onChange: (companyId: number | undefined) => void
}) => {
  const { companies, isLoading } = useCompany()
  const options: DropdownOption[] = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id.toString(),
        label: company.name,
        fullName: company.fullName ?? companyLabelWithHierarchy(company, companies),
      })),
    [companies]
  )
  const selected = useMemo(() => options.find((o) => o.value === companyId?.toString()) ?? null, [options, companyId])

  return (
    <div className="w-full sm:w-[23rem]" data-testid="geofence-company">
      <Dropdown
        options={options}
        selectedItem={selected}
        handleSingleSelectItem={(o) => onChange(o ? parseInt(o.value) : undefined)}
        placeholder={isLoading ? 'Loading...' : 'Choose a company...'}
      />
    </div>
  )
}
